/**
 * The report document, rendered.
 *
 * Every report the api serves arrives in one shape - sections, columns, rows -
 * so one table, one date picker and one set of cell formats cover all of them,
 * and Export and Print work everywhere for free.
 *
 * Lifted out of ReportsPage so the Advertising page can show the same document
 * to somebody who may see nothing else. Two copies of this table would drift,
 * and the pinned first column and the money brackets are the parts of it she
 * asked for by name.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronDown } from "lucide-react";
import { Button, Input } from "@/components/ui";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

// ─── Types (mirror of the API's report document) ───────────────────────────

/**
 * `link` is a cell that goes somewhere: the row carries the text under the
 * column's own key and the destination under `<key>_href`.
 *
 *   "can we make it so I can click on name from the list and be brought to
 *    the chart?"
 *
 * Any report can use it, rather than one table learning to be clickable.
 */
export type ColType = "text" | "money" | "int" | "pct" | "hours" | "date" | "datetime" | "link";

export interface Col { key: string; label: string; type?: ColType; sticky?: boolean }
export interface Section {
  key: string;
  title?: string;
  columns: Col[];
  rows: Record<string, unknown>[];
  total?: Record<string, unknown>;
  note?: string;
  empty?: string;
}
export interface ReportDoc {
  key: string;
  title: string;
  subtitle?: string;
  sections: Section[];
  range: { from: string; to: string; label: string; timezone: string };
}
export interface CatalogEntry { key: string; name: string; blurb: string }
export interface CatalogGroup { group: string; reports: CatalogEntry[] }

// ─── Date presets ──────────────────────────────────────────────────────────
// The same set Boulevard offers, in the same order, because that's the muscle
// memory she already has.

export const PRESETS: { key: string; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "Week to date" },
  { key: "last_week", label: "Last week" },
  { key: "month", label: "Month to date" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "year", label: "Year to date" },
  { key: "last_year", label: "Last year" },
];

// ─── Cell formatting ───────────────────────────────────────────────────────

export function formatCell(value: unknown, type?: ColType): string {
  if (value === null || value === undefined || value === "") return type ? "" : "—";
  switch (type) {
    case "money": {
      const cents = Number(value);
      if (!Number.isFinite(cents)) return String(value);
      // Refunds come through negative; show them in brackets, as an
      // accountant would, rather than with a minus sign.
      return cents < 0 ? `(${formatMoney(Math.abs(cents))})` : formatMoney(cents);
    }
    case "int": return Number(value).toLocaleString();
    case "pct": return `${Number(value)}%`;
    case "hours": return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
    case "date": {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value)
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    }
    case "datetime": {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value)
        : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    }
    default: return String(value);
  }
}

export const isNumeric = (t?: ColType) => t === "money" || t === "int" || t === "pct" || t === "hours";

export function ReportTable({ section, filter }: { section: Section; filter: string }) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const rows = useMemo(() => {
    let out = section.rows;
    if (filter.trim()) {
      const q = filter.toLowerCase();
      out = out.filter((r) => section.columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q)));
    }
    if (sort) {
      const col = section.columns.find((c) => c.key === sort.key);
      out = [...out].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        if (isNumeric(col?.type)) return (Number(av ?? 0) - Number(bv ?? 0)) * sort.dir;
        return String(av ?? "").localeCompare(String(bv ?? "")) * sort.dir;
      });
    }
    return out;
  }, [section, filter, sort]);

  const toggleSort = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: -1 }));

  /**
   * Where each pinned column sits.
   *
   *   "is it possible when I look at every inquiry newest first that the names
   *    stay in place as I scroll side to see dollar amounts and who followed
   *    thru"
   *
   * One pinned column could be `left-0` and nothing else. Two or more have to
   * know how wide the ones before them are, and that is a measurement, not a
   * number we can write down: "Came in" is as wide as the longest date in it,
   * and "Client" as wide as the longest name. So the header cells are measured
   * and each pinned column is offset by the width of its pinned neighbours.
   * Re-measured when the table resizes, because the first paint is usually
   * before the fonts land.
   */
  const headRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [lefts, setLefts] = useState<Record<string, number>>({});
  const pinned = useMemo(() => section.columns.filter((c) => c.sticky).map((c) => c.key), [section.columns]);
  const lastPinned = pinned.length > 0 ? pinned[pinned.length - 1] : null;

  useEffect(() => {
    const measure = () => {
      let x = 0;
      const next: Record<string, number> = {};
      for (const key of pinned) {
        next[key] = x;
        x += headRefs.current[key]?.getBoundingClientRect().width ?? 0;
      }
      setLefts((prev) => {
        const same = Object.keys(next).length === Object.keys(prev).length
          && Object.entries(next).every(([k, v]) => Math.abs((prev[k] ?? -1) - v) < 0.5);
        return same ? prev : next;
      });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    for (const key of pinned) { const el = headRefs.current[key]; if (el) ro.observe(el); }
    return () => ro.disconnect();
  }, [pinned, rows]);

  /** The shared look of a pinned cell: the offset, and an edge on the last one. */
  const pinStyle = (c: Col) => (c.sticky ? { left: lefts[c.key] ?? 0 } : undefined);
  const pinClass = (c: Col, bg: string) =>
    c.sticky && [bg, "print:static print:bg-transparent", c.key === lastPinned ? "shadow-[1px_0_0_0_rgb(63_63_70/0.7)]" : ""].join(" ");

  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      {section.title && (
        <div className="border-b border-zinc-800/60 px-4 py-2.5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{section.title}</h3>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-zinc-500">
          {filter.trim() ? "Nothing matches that filter." : (section.empty ?? "No rows.")}
        </p>
      ) : (
        // The pinned columns survive a sideways scroll — the single thing she
        // complained about in Boulevard, and again on 4 Nov when the names
        // slid away as she scrolled out to the money.
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="border-b border-zinc-800/60 text-zinc-500">
                {section.columns.map((c) => (
                  <th
                    key={c.key}
                    ref={(el) => { headRefs.current[c.key] = el; }}
                    onClick={() => toggleSort(c.key)}
                    style={pinStyle(c)}
                    className={cn(
                      "cursor-pointer select-none whitespace-nowrap px-3 py-2 text-[11px] font-medium uppercase tracking-wider hover:text-zinc-300",
                      isNumeric(c.type) ? "text-right" : "text-left",
                      c.sticky && "sticky z-20",
                      pinClass(c, "bg-zinc-900"),
                    )}
                  >
                    {c.label}
                    {sort?.key === c.key && <span className="ml-1 text-indigo-400">{sort.dir === 1 ? "↑" : "↓"}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="group border-b border-zinc-800/30 last:border-0 hover:bg-zinc-800/40">
                  {section.columns.map((c) => (
                    <td
                      key={c.key}
                      style={pinStyle(c)}
                      className={cn(
                        "whitespace-nowrap px-3 py-2 tabular-nums",
                        isNumeric(c.type) ? "text-right text-zinc-300" : "text-zinc-300",
                        c.sticky && "sticky z-10 font-medium text-zinc-100 group-hover:bg-zinc-800",
                        pinClass(c, "bg-zinc-900"),
                      )}
                    >
                      {c.type === "link" && typeof row[`${c.key}_href`] === "string"
                        ? (
                          <Link
                            to={String(row[`${c.key}_href`])}
                            className="text-indigo-300 underline-offset-2 hover:text-indigo-200 hover:underline"
                          >
                            {formatCell(row[c.key], "text")}
                          </Link>
                        )
                        : formatCell(row[c.key], c.type)}
                    </td>
                  ))}
                </tr>
              ))}
              {section.total && (
                <tr className="border-t-2 border-zinc-700 bg-zinc-900/60 font-semibold">
                  {section.columns.map((c) => (
                    <td
                      key={c.key}
                      style={pinStyle(c)}
                      className={cn(
                        "whitespace-nowrap px-3 py-2 tabular-nums text-zinc-100",
                        isNumeric(c.type) ? "text-right" : "text-left",
                        c.sticky && "sticky z-10",
                        pinClass(c, "bg-zinc-900"),
                      )}
                    >
                      {formatCell(section.total![c.key], c.type)}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {section.note && (
        <p className="border-t border-zinc-800/60 px-4 py-2 text-[11px] leading-relaxed text-zinc-500">
          {section.note}
        </p>
      )}
    </div>
  );
}

// ─── Date range picker ─────────────────────────────────────────────────────

export function DateRangePicker({
  preset, custom, onPreset, onCustom, label,
}: {
  preset: string;
  custom: { from: string; to: string } | null;
  onPreset: (p: string) => void;
  onCustom: (from: string, to: string) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(custom?.from ?? "");
  const [to, setTo] = useState(custom?.to ?? "");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg border border-zinc-700/70 bg-zinc-900/60 px-2.5 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800"
      >
        <Calendar className="h-3.5 w-3.5 text-zinc-500" />
        <span className="max-w-[14rem] truncate">{label || "Pick a range"}</span>
        <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-1 w-72 rounded-xl border border-zinc-700/70 bg-zinc-900 p-2 shadow-xl">
          <div className="grid grid-cols-2 gap-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => { onPreset(p.key); setOpen(false); }}
                className={cn(
                  "rounded-md px-2 py-1.5 text-left text-xs",
                  !custom && preset === p.key
                    ? "bg-indigo-500/20 text-indigo-200"
                    : "text-zinc-300 hover:bg-zinc-800",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="mt-2 border-t border-zinc-800 pt-2">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Or pick your own dates
            </p>
            <div className="flex items-center gap-1.5">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 text-xs" />
              <span className="text-zinc-600">–</span>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-xs" />
            </div>
            <Button
              size="sm" className="mt-2 w-full"
              disabled={!from || !to}
              onClick={() => { onCustom(from, to); setOpen(false); }}
            >
              Show these dates
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
